import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { SignalButton, State } from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { OnboardingSurveyApiResponse } from '@core/api/workspace/models';

@Component({
  selector: 'app-onboarding-page',
  imports: [RouterLink, SignalButton],
  templateUrl: './onboarding-page.html',
  styleUrl: './onboarding-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingPage extends BaseComponent {
  private readonly userAPIService = inject(WorkspaceAPIService);

  readonly currentYear = new Date().getFullYear();
  readonly currentStep = signal(1);

  readonly selectedIndustry = signal<string | null>(null);
  readonly selectedUseCases = signal<string[]>([]);
  readonly selectedTeamSize = signal<string | null>(null);
  readonly selectedHeardAbout = signal<string | null>(null);

  readonly industryState = new State<OnboardingSurveyApiResponse>();
  readonly useCasesState = new State<OnboardingSurveyApiResponse>();
  readonly teamSizeState = new State<OnboardingSurveyApiResponse>();
  readonly heardAboutState = new State<OnboardingSurveyApiResponse>();
  readonly skipState = new State<OnboardingSurveyApiResponse>();

  readonly isLoading = computed(
    () =>
      this.industryState.loading() ||
      this.useCasesState.loading() ||
      this.teamSizeState.loading() ||
      this.heardAboutState.loading() ||
      this.skipState.loading(),
  );

  readonly canContinue = computed(() => {
    switch (this.currentStep()) {
      case 1:
        return this.selectedIndustry() !== null;
      case 2:
        return this.selectedUseCases().length > 0;
      case 3:
        return this.selectedTeamSize() !== null;
      case 4:
        return this.selectedHeardAbout() !== null;
      default:
        return false;
    }
  });

  readonly industryOptions = [
    'SaaS / Tech',
    'E-commerce',
    'Healthcare',
    'Finance',
    'Education',
    'Marketing / Agency',
    'Other',
  ];

  readonly useCaseOptions = [
    'Lead capture forms',
    'Customer surveys',
    'Employee onboarding',
    'Event registrations',
    'Feedback collection',
    'Other',
  ];

  readonly teamSizeOptions = ['Just me', '2–10', '11–50', '51–200', '200+'];

  readonly heardAboutOptions = [
    'Google Search',
    'LinkedIn',
    'Twitter / X',
    'Product Hunt',
    'Word of mouth',
    'Other',
  ];

  goBack(): void {
    if (this.currentStep() > 1) {
      this.currentStep.update((s) => s - 1);
    }
  }

  toggleUseCase(value: string): void {
    this.selectedUseCases.update((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    );
  }

  isUseCaseSelected(value: string): boolean {
    return this.selectedUseCases().includes(value);
  }

  onContinue(): void {
    switch (this.currentStep()) {
      case 1:
        this.submitIndustry();
        break;
      case 2:
        this.submitUseCases();
        break;
      case 3:
        this.submitTeamSize();
        break;
      case 4:
        this.submitHeardAbout();
        break;
    }
  }

  onSkip(): void {
    this.executeRequest({
      state: this.skipState,
      request: this.userAPIService.skipOnboardingSurvey(),
      handleError: true,
      onSuccess: () => this.router.navigate(['/dashboard']),
    });
  }

  private submitIndustry(): void {
    const industry = this.selectedIndustry();
    if (!industry) return;
    this.executeRequest({
      state: this.industryState,
      request: this.userAPIService.updateOnboardingIndustry({ industry }),
      handleError: true,
      onSuccess: () => this.currentStep.set(2),
    });
  }

  private submitUseCases(): void {
    const plannedUseCases = this.selectedUseCases();
    if (plannedUseCases.length === 0) return;
    this.executeRequest({
      state: this.useCasesState,
      request: this.userAPIService.updateOnboardingPlannedUseCases({ plannedUseCases }),
      handleError: true,
      onSuccess: () => this.currentStep.set(3),
    });
  }

  private submitTeamSize(): void {
    const teamSize = this.selectedTeamSize();
    if (!teamSize) return;
    this.executeRequest({
      state: this.teamSizeState,
      request: this.userAPIService.updateOnboardingTeamSize({ teamSize }),
      handleError: true,
      onSuccess: () => this.currentStep.set(4),
    });
  }

  private submitHeardAbout(): void {
    const heardAboutUs = this.selectedHeardAbout();
    if (!heardAboutUs) return;
    this.executeRequest({
      state: this.heardAboutState,
      request: this.userAPIService.updateOnboardingHeardAbout({ heardAboutUs }),
      handleError: true,
      onSuccess: () => this.router.navigate(['/dashboard']),
    });
  }
}
